import { PreviewSandbox } from './previewSandbox';
import { buildCanvasContext, collectLiveTargets, bindPlanToLiveTargets } from './context';
import { executePlan } from './tools/executor';
import renderButtonToImages from './buttonRenderer';
import { buildSystemPrompt, parsePlanResponse, validatePlanShape } from './llmService';

export function createCanvasAiContext(options) {
  const {
    store,
    request,
    hooks = {},
    sandbox = new PreviewSandbox(store),
  } = options;

  return {
    store,
    request,
    hooks,
    sandbox,
    renderButton: renderButtonToImages,
    lastCreatedId: null,
  };
}

export async function runPlan(plan, ctx, options = {}) {
  const { previewMode = false, onlyMeta = false } = options;
  let steps = [...(plan.steps || [])];

  if (onlyMeta) {
    const tool = steps[0] && (steps[0].tool || steps[0].name);
    return executePlan(steps, ctx);
  }

  steps = steps.filter((s) => {
    const t = s.tool || s.name;
    return t !== 'plan.preview' && t !== 'plan.apply' && t !== 'plan.discard';
  });

  if (previewMode && ctx.sandbox && !ctx.sandbox.isActive()) {
    ctx.sandbox.capture();
  }
  ctx.previewMode = !!(previewMode || (ctx.sandbox && ctx.sandbox.isActive()));
  const bound = bindPlanToLiveTargets(
    { summary: plan.summary, steps },
    ctx.store,
    (ctx.pinnedAreaIds && ctx.pinnedAreaIds.length)
      ? ctx.pinnedAreaIds
      : (plan.targetIds || [])
  );
  const result = await executePlan(bound.steps || [], ctx);
  return result;
}

export async function chatAndPlan(userMessage, ctx, chatFn, history = []) {
  const canvasContext = buildCanvasContext(ctx.store, ctx.hooks);
  const live = collectLiveTargets(ctx.store, ctx.pinnedAreaIds);
  const livePayload = live.map((c) => ({
    id: String(c.id),
    component: c.component,
    alias: c.style && c.style.alias,
    text: typeof c.propValue === 'string' ? c.propValue : undefined,
  }));
  const liveText = live.length
    ? `画布已锁定用户点选/框选的组件，必须只改这些 id（一律当字符串用）：${JSON.stringify(livePayload)}。禁止回复找不到/未找到组件，禁止编造其它 id。`
    : '画布当前没有点选或框选组件。若用户说选中的，请提示先点选。';
  const systemPrompt = buildSystemPrompt(2);
  const messages = [
    { role: 'system', content: systemPrompt },
    {
      role: 'user',
      content: `${liveText}\n\n当前画布上下文：\n${JSON.stringify(canvasContext)}\n\n用户请求：${userMessage}`,
    },
    ...history,
  ];
  const response = await chatFn({ messages, canvasContext });
  const content = response.content || response.message || response;
  if (response.plan) {
    const shapeError = validatePlanShape(response.plan);
    if (shapeError) {
      return {
        ok: false,
        message: `模型返回的 JSON 格式不正确：${shapeError}`,
        raw: response,
      };
    }
    return { ok: true, plan: response.plan, raw: response, model: response.model };
  }
  const parsed = parsePlanResponse(content);
  if (!parsed.ok) {
    return { ok: false, message: parsed.error || '模型返回的 JSON 格式不正确', raw: response };
  }
  return { ok: true, plan: parsed.plan, raw: response, model: response.model };
}

export {
  PreviewSandbox,
  buildCanvasContext,
  executePlan,
  renderButtonToImages,
  buildSystemPrompt,
  parsePlanResponse,
};
